import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import './FootprintPainting.css';

// Tiny SVG placeholder - shows instantly
const CANVAS_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23fdfcfa" width="300" height="300"/%3E%3C/svg%3E';

const FootprintPainting = () => {
  const navigate = useNavigate();
  const [canvasSnapshot, setCanvasSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const snapshotRef = ref(database, 'feetWatercolor/snapshot');
    const unsubscribe = onValue(snapshotRef, (snapshot) => {
      if (snapshot.exists()) {
        setCanvasSnapshot(snapshot.val());
        setImageLoaded(false); // Reset when new snapshot arrives
      } else {
        setCanvasSnapshot(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="footprint-painting">
      <motion.div
        className="footprint-frame"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate('/feet-watercolor')}
        transition={{ duration: 0.3 }}
      >
        <div className="footprint-frame-border">
          <div className="footprint-frame-inner">
            {canvasSnapshot ? (
              <>
                {/* Placeholder - shows immediately */}
                {!imageLoaded && (
                  <img
                    src={CANVAS_PLACEHOLDER}
                    alt="Loading canvas..."
                    className="footprint-canvas-image placeholder-canvas"
                  />
                )}
                {/* Real canvas - fades in when loaded */}
                <motion.img
                  key={canvasSnapshot.substring(0, 50)}
                  src={canvasSnapshot}
                  alt="Footprint Canvas"
                  className="footprint-canvas-image"
                  onLoad={() => setImageLoaded(true)}
                  style={{
                    opacity: imageLoaded ? 1 : 0,
                    position: imageLoaded ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: imageLoaded ? 1 : 0 }}
                  transition={{ duration: 0.8 }}
                />
              </>
            ) : loading ? (
              <div className="footprint-loading">
                <div className="footprint-spinner" />
              </div>
            ) : (
              <div className="footprint-placeholder">
                <svg viewBox="0 0 80 75" className="footprint-icon">
                  {/* Left footprint */}
                  <g transform="translate(2, 8)">
                    <path
                      d="M 12,52 C 8,48 6,42 7,35 C 8,28 12,22 16,20 C 20,18 24,20 26,24 C 28,28 28,35 27,42 C 26,49 22,54 17,55 C 14,55 12,54 12,52 Z"
                      fill="#7eb8d8"
                      opacity="0.6"
                    />
                    <ellipse cx="10" cy="16" rx="5" ry="7" fill="#7eb8d8" opacity="0.65" />
                    <ellipse cx="17" cy="12" rx="4" ry="6.5" fill="#9ec5db" opacity="0.7" />
                    <ellipse cx="23" cy="11" rx="3.5" ry="6" fill="#7eb8d8" opacity="0.65" />
                    <ellipse cx="28" cy="13" rx="3" ry="5" fill="#9ec5db" opacity="0.6" />
                  </g>
                  {/* Right footprint */}
                  <g transform="translate(40, 5)">
                    <path
                      d="M 26,55 C 30,51 32,45 31,38 C 30,31 26,25 22,23 C 18,21 14,23 12,27 C 10,31 10,38 11,45 C 12,52 16,57 21,58 C 24,58 26,57 26,55 Z"
                      fill="#d4a0b8"
                      opacity="0.6"
                    />
                    <ellipse cx="28" cy="19" rx="5" ry="7" fill="#d4a0b8" opacity="0.65" />
                    <ellipse cx="21" cy="15" rx="4" ry="6.5" fill="#e8b8cc" opacity="0.7" />
                    <ellipse cx="15" cy="14" rx="3.5" ry="6" fill="#d4a0b8" opacity="0.65" />
                    <ellipse cx="10" cy="16" rx="3" ry="5" fill="#e8b8cc" opacity="0.6" />
                  </g>
                </svg>
                <span>Start painting!</span>
              </div>
            )}
          </div>

          {/* Frame decorations */}
          <div className="footprint-corner footprint-corner-tl" />
          <div className="footprint-corner footprint-corner-tr" />
          <div className="footprint-corner footprint-corner-bl" />
          <div className="footprint-corner footprint-corner-br" />
        </div>

        <div className="footprint-label">
          Footprint Canvas
        </div>
      </motion.div>
    </div>
  );
};

export default FootprintPainting;
